// src/pages/task/TaskStatsCharts.tsx
//
// Presentational chart building blocks for TaskStatsPage. Plain HTML/CSS bars
// (no charting dependency exists in this repo — see TaskDependencyGraphPage
// for the SVG equivalent used when marks need free-form layout; a horizontal
// bar list needs none of that).
//
// Color sourcing follows the dataviz skill (`/dataviz` skill references):
//   - Status roles (open/in-progress/done/blocked) reuse this app's existing
//     generated design tokens (@core/ui/tokens `colors`) rather than inventing
//     new hues — these already carry the same meaning elsewhere (StatusBadge,
//     board columns in features/task/logic.ts).
//   - Categorical (task type) and ordinal (priority) hues have no equivalent
//     token in this app yet, so they use the skill's validated default palette
//     (references/palette.md) verbatim, run through
//     scripts/validate_palette.js for both light and dark chart surfaces
//     (#ffffff / #202122, this app's --card) — see PR notes. Adjacent pairs
//     pass CVD ΔE >= 8.4 and normal-vision floor >= 19.3 in both modes; three
//     light-mode slots sit below 3:1 contrast by design, mitigated here by
//     always-on direct labels + a table-view fallback (the skill's relief
//     requirement).
//   - "Other" (folded tail of the type distribution) and unresolved-assignee
//     bars use a neutral de-emphasis gray, never a categorical hue.
//
// No React import needed with the automatic JSX runtime.
import { useId, useState } from 'react';
import { cn } from '@core/ui';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@react/components';

// ---------------------------------------------------------------------------
// Palette (see header comment for provenance)
// ---------------------------------------------------------------------------

export interface ModeColor {
  light: string;
  dark: string;
}

/** Fixed categorical hues, slots 1-7 (slot 8 reserved / unused here — the
 * type distribution folds its tail into "Other" instead of spending it). */
export const CATEGORICAL: ModeColor[] = [
  { light: '#2a78d6', dark: '#3987e5' }, // 1 blue
  { light: '#eb6834', dark: '#d95926' }, // 2 orange
  { light: '#1baf7a', dark: '#199e70' }, // 3 aqua
  { light: '#eda100', dark: '#c98500' }, // 4 yellow
  { light: '#e87ba4', dark: '#d55181' }, // 5 magenta
  { light: '#008300', dark: '#008300' }, // 6 green
  { light: '#4a3aa7', dark: '#9085e9' }, // 7 violet
];

/** Neutral de-emphasis gray — "Other" buckets, unresolved identities. */
export const NEUTRAL: ModeColor = { light: '#9ca3af', dark: '#7a7d84' };

/** Ordinal ramp for task priority (lowest -> highest). One hue, monotone
 * lightness; the dark column flips anchor per the skill's sequential rule so
 * neither end recedes into its own surface. */
export const PRIORITY_ORDINAL: Record<string, ModeColor> = {
  TASK_PRIORITY_LOWEST: { light: '#86b6ef', dark: '#184f95' },
  TASK_PRIORITY_LOW: { light: '#5598e7', dark: '#256abf' },
  TASK_PRIORITY_MEDIUM: { light: '#2a78d6', dark: '#3987e5' },
  TASK_PRIORITY_HIGH: { light: '#1c5cab', dark: '#6da7ec' },
  TASK_PRIORITY_HIGHEST: { light: '#104281', dark: '#9ec5f4' },
};

/** Fixed type -> slot assignment (identity, not rank — stays put regardless
 * of which subset a given period/filter actually contains). */
export const TYPE_SLOT: Record<string, ModeColor> = {
  TASK_TYPE_TASK: CATEGORICAL[0],
  TASK_TYPE_BUG: CATEGORICAL[1],
  TASK_TYPE_FEATURE: CATEGORICAL[2],
  TASK_TYPE_IMPROVEMENT: CATEGORICAL[3],
  TASK_TYPE_SUPPORT: CATEGORICAL[4],
  TASK_TYPE_QUESTION: CATEGORICAL[5],
  TASK_TYPE_SUBTASK: CATEGORICAL[6],
};

/** Status roles, reusing this app's existing generated tokens
 * (@core/ui/tokens colors.primary/warning/success/error) rather than the
 * dataviz categorical set — these are a fixed, reserved "state" palette. */
export const STATUS_COLOR = {
  open: { light: '#3b82f6', dark: '#3b82f6' } as ModeColor,
  inProgress: { light: '#d97706', dark: '#f59e0b' } as ModeColor,
  done: { light: '#16a34a', dark: '#22c55e' } as ModeColor,
  blocked: { light: '#ef4444', dark: '#ef4444' } as ModeColor,
};

export function pickColor(c: ModeColor, isDark: boolean): string {
  return isDark ? c.dark : c.light;
}

// ---------------------------------------------------------------------------
// Stat tile
// ---------------------------------------------------------------------------

export interface StatTileProps {
  label: string;
  value: string;
  hint?: string;
  tone?: 'neutral' | 'good' | 'warning' | 'critical';
}

const TONE_CLASS: Record<NonNullable<StatTileProps['tone']>, string> = {
  neutral: 'text-foreground',
  good: 'text-[#16a34a] dark:text-[#22c55e]',
  warning: 'text-[#d97706] dark:text-[#f59e0b]',
  critical: 'text-destructive',
};

/** KPI tile per the dataviz stat-tile contract: label (sentence case, no
 * trailing colon), value (proportional figures, never tabular-nums at this
 * size), optional hint. */
export function StatTile({ label, value, hint, tone = 'neutral' }: StatTileProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
        <CardTitle className={cn('text-3xl [font-variant-numeric:normal]', TONE_CLASS[tone])}>
          {value}
        </CardTitle>
        {hint ? <p className="text-xs text-muted-foreground mt-1">{hint}</p> : null}
      </CardHeader>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Bar list chart
// ---------------------------------------------------------------------------

export interface BarItem {
  key: string;
  label: string;
  value: number;
  color: string;
  /** Shown in the tooltip/table secondary column instead of the raw label,
   * e.g. an unresolved assignee's truncated id with a note. */
  note?: string;
}

export interface LegendEntry {
  label: string;
  color: string;
}

export interface BarChartProps {
  items: BarItem[];
  /** Denominator for the percentage shown in the tooltip/table; defaults to
   * the sum of item values. */
  total?: number;
  valueLabel: string;
  percentLabel: (pct: number) => string;
  emptyMessage: string;
  tableToggleLabel: string;
  legend?: LegendEntry[];
  /** Rendered under the chart, e.g. "Lowest -> Highest" for an ordinal ramp. */
  axisCaption?: string;
  columnLabel?: string;
}

/** Horizontal bar-list chart: thin bars, rounded data-end, direct value
 * label at the tip, hover/focus tooltip, and a same-data table view for
 * accessibility (the WCAG-clean twin every chart needs per the skill). */
export function BarChart({
  items,
  total,
  valueLabel,
  percentLabel,
  emptyMessage,
  tableToggleLabel,
  legend,
  axisCaption,
  columnLabel,
}: BarChartProps) {
  const [hovered, setHovered] = useState<string | null>(null);
  const uid = useId();

  if (items.length === 0) {
    return <ChartEmptyState message={emptyMessage} />;
  }

  const denom = total ?? items.reduce((sum, it) => sum + it.value, 0);
  const maxValue = Math.max(...items.map((it) => it.value), 1);

  return (
    <div>
      {legend && legend.length > 0 ? (
        <ul className="flex flex-wrap gap-x-4 gap-y-1.5 mb-4" aria-hidden="true">
          {legend.map((entry) => (
            <li key={entry.label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm flex-shrink-0"
                style={{ backgroundColor: entry.color }}
              />
              {entry.label}
            </li>
          ))}
        </ul>
      ) : null}

      <ul className="space-y-3">
        {items.map((item) => {
          const pct = denom > 0 ? item.value / denom : 0;
          const widthPct = Math.max(0, Math.min(100, (item.value / maxValue) * 100));
          const isHovered = hovered === item.key;
          const tooltipId = `${uid}-${item.key}`;

          return (
            <li
              key={item.key}
              className="relative"
              onMouseEnter={() => setHovered(item.key)}
              onMouseLeave={() => setHovered((h) => (h === item.key ? null : h))}
            >
              <button
                type="button"
                aria-label={`${item.label}: ${item.value} ${valueLabel} (${percentLabel(Math.round(pct * 100))})`}
                aria-describedby={isHovered ? tooltipId : undefined}
                className="w-full flex items-center gap-3 rounded-sm border-0 bg-transparent p-0 cursor-default text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onFocus={() => setHovered(item.key)}
                onBlur={() => setHovered((h) => (h === item.key ? null : h))}
              >
                <span className="w-28 flex-shrink-0 truncate text-sm text-foreground" title={item.note || item.label}>
                  {item.label}
                </span>
                <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full transition-[width] duration-200"
                    style={{
                      width: `${widthPct}%`,
                      backgroundColor: item.color,
                      borderRadius: '2px 4px 4px 2px',
                    }}
                  />
                </div>
                <span className="w-10 flex-shrink-0 text-right text-sm font-medium text-foreground [font-variant-numeric:tabular-nums]">
                  {item.value}
                </span>
              </button>

              {isHovered ? (
                <div
                  id={tooltipId}
                  role="tooltip"
                  className="absolute left-28 -top-1 -translate-y-full z-10 rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs shadow-md whitespace-nowrap"
                >
                  <span className="font-semibold text-popover-foreground">{item.value} {valueLabel}</span>
                  <span className="text-muted-foreground"> · {percentLabel(Math.round(pct * 100))}</span>
                  {item.note ? <div className="text-muted-foreground mt-0.5">{item.note}</div> : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {axisCaption ? (
        <p className="mt-3 text-xs text-muted-foreground text-center">{axisCaption}</p>
      ) : null}

      <details className="mt-4 group">
        <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground select-none list-none flex items-center gap-1">
          <svg
            className="h-3 w-3 transition-transform group-open:rotate-90"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
            strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
          {tableToggleLabel}
        </summary>
        <div className="mt-2 overflow-x-auto">
          <Table compact striped>
            <TableHeader>
              <TableRow>
                <TableHead>{columnLabel || valueLabel}</TableHead>
                <TableHead className="text-right">{valueLabel}</TableHead>
                <TableHead className="text-right">%</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => {
                const pct = denom > 0 ? Math.round((item.value / denom) * 100) : 0;
                return (
                  <TableRow key={item.key}>
                    <TableCell>{item.note ? `${item.label} (${item.note})` : item.label}</TableCell>
                    <TableCell className="text-right [font-variant-numeric:tabular-nums]">{item.value}</TableCell>
                    <TableCell className="text-right [font-variant-numeric:tabular-nums]">{pct}%</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </details>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

export function ChartEmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
      <svg
        className="h-8 w-8 text-muted-foreground"
        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
        strokeLinecap="round" strokeLinejoin="round"
      >
        <path d="M3 3v18h18M8 17V10m5 7V6m5 11v-4" />
      </svg>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}
